import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { CaseSummary } from '../types';

type TimeEntryRow = {
  id: string;
  caseId: string | null;
  userId: string;
  startedAt: string;
  endedAt: string | null;
  minutes: number;
  description: string | null;
  billable: boolean;
  hourlyRateIls: number | null;
  invoiceId: string | null;
  createdAt: string;
  updatedAt: string;
  case: { id: string; caseNumber: string; area: string | null; hourlyRateIls: number | null } | null;
  user: { id: string; name: string | null; email: string };
};

type ListResponse = { items: TimeEntryRow[] };

/**
 * Time-entries listing + quick-log form. Lives at /time, surfaced from
 * the sidebar so logging time isn't buried inside ClientDetail.
 */
export function TimePage() {
  const { t, i18n } = useTranslation(['time', 'common']);
  const [items, setItems] = useState<TimeEntryRow[]>([]);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billableFilter, setBillableFilter] = useState<'all' | 'billable' | 'unbilled'>('all');
  const [caseFilter, setCaseFilter] = useState<'any' | string>('any');

  // Quick-log form (manual minutes, mirrors TimeEntryWidget but no timer)
  const [logCaseId, setLogCaseId] = useState('');
  const [logMinutes, setLogMinutes] = useState('');
  const [logDesc, setLogDesc] = useState('');
  const [logBillable, setLogBillable] = useState(true);
  const [logBusy, setLogBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (billableFilter === 'billable') params.set('billable', 'true');
      if (billableFilter === 'unbilled') params.set('unbilled', 'true');
      if (caseFilter !== 'any') params.set('caseId', caseFilter);
      const d = await apiFetch<ListResponse>(`/api/time-entries?${params}`);
      setItems(d.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billableFilter, caseFilter]);

  useEffect(() => {
    apiFetch<{ items: CaseSummary[] }>('/api/cases?status=open')
      .then((d) => setCases(d.items))
      .catch(() => setCases([]));
  }, []);

  async function handleLog(e: FormEvent) {
    e.preventDefault();
    const minutes = Number(logMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    setLogBusy(true);
    try {
      await apiFetch('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          caseId: logCaseId || null,
          minutes,
          description: logDesc.trim() || null,
          billable: logBillable,
        }),
      });
      setLogMinutes('');
      setLogDesc('');
      void load();
    } finally {
      setLogBusy(false);
    }
  }

  // Aggregate: this-month minutes + unbilled minutes
  const stats = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    let monthMin = 0;
    let unbilledMin = 0;
    for (const e of items) {
      if (new Date(e.startedAt) >= monthStart) monthMin += e.minutes;
      if (e.billable && !e.invoiceId) unbilledMin += e.minutes;
    }
    return { monthMin, unbilledMin };
  }, [items]);

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('metrics.this_month')}</div>
          <div className="metric-val">
            {t('metrics.hours', { hours: Math.round((stats.monthMin / 60) * 10) / 10 })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.unbilled_minutes')}</div>
          <div className="metric-val">
            {t('metrics.hours', { hours: Math.round((stats.unbilledMin / 60) * 10) / 10 })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('metrics.total_entries')}</div>
          <div className="metric-val">{items.length}</div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('new.title')}</div>
        <form onSubmit={handleLog} style={{ padding: 14, display: 'grid', gap: 10 }}>
          <div className="field-grid">
            <div className="field">
              <label>{t('new.case_label')}</label>
              <select value={logCaseId} onChange={(e) => setLogCaseId(e.target.value)}>
                <option value="">{t('filters.any_case')}</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.caseNumber} {c.client?.name ? `· ${c.client.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('new.minutes_label')}</label>
              <input
                type="number"
                min={1}
                max={1440}
                value={logMinutes}
                onChange={(e) => setLogMinutes(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="field">
            <label>{t('new.description_label')}</label>
            <input value={logDesc} onChange={(e) => setLogDesc(e.target.value)} />
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)' }}
          >
            <input
              type="checkbox"
              checked={logBillable}
              onChange={(e) => setLogBillable(e.target.checked)}
            />
            {t('new.billable_label')}
          </label>
          <div>
            <button
              type="submit"
              className="btn-primary"
              disabled={logBusy || !logMinutes}
            >
              {t('new.submit')}
            </button>
          </div>
        </form>
      </div>

      <div className="filter-row" style={{ marginBottom: 14, gap: 8 }}>
        {(['all', 'billable', 'unbilled'] as const).map((f) => (
          <div
            key={f}
            className={`type-btn ${billableFilter === f ? 'active' : ''}`}
            onClick={() => setBillableFilter(f)}
            style={{ flex: 'unset', padding: '6px 14px' }}
          >
            {t(`filters.${f}`)}
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <select
          className="inline-select"
          value={caseFilter}
          onChange={(e) => setCaseFilter(e.target.value)}
        >
          <option value="any">
            {t('filters.case')} {t('filters.any_case')}
          </option>
          {cases.map((c) => (
            <option key={c.id} value={c.id}>
              {c.caseNumber}
            </option>
          ))}
        </select>
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('title')}</div>
        {loading ? (
          <div className="empty">{t('loading')}</div>
        ) : error ? (
          <div className="empty">{t('error')}</div>
        ) : items.length === 0 ? (
          <div className="empty">{t('empty')}</div>
        ) : (
          items.map((e) => (
            <div key={e.id} className="case-row">
              <div className="case-info">
                <div className="case-name">
                  {Math.round((e.minutes / 60) * 10) / 10}h
                  {e.case ? ` · ${e.case.caseNumber}` : ` · ${t('row.no_case')}`}
                </div>
                <div className="case-sub">
                  {[e.description, e.user.name ?? e.user.email]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {new Date(e.startedAt).toLocaleDateString(i18n.language, {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <span className={`badge ${e.billable ? 'b-open' : 'b-closed'}`}>
                {e.billable ? t('row.billable_yes') : t('row.billable_no')}
              </span>
              {e.invoiceId && <span className="badge b-pending">{t('row.locked')}</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
